import React from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import { scaleLinear, scalePoint } from 'd3-scale'
import { curveMonotoneX, line as d3line } from 'd3-shape'
import Loader from 'src/designSystem/Loader'
import { DateTime } from 'luxon'
import { useAdvancedPatientHistoryState } from '../api/useAdvancedPatientHistory'
import { formatDay } from '../format'
import { onDark } from '../theme'
import EmptyNote from './EmptyNote'
import Section from './Section'
import {
  SCORE_TYPES,
  ScorePoint,
  ScoreType,
  TotalChangeTone,
  scorePoints,
  totalChangeLabel,
  totalChangePercent,
  totalChangeTone,
} from './scoreHistory'

const WIDTH = 300
const HEIGHT = 120
const PADDING = { top: 18, right: 16, bottom: 22, left: 16 }

const shortDay = (iso: string): string => DateTime.fromISO(iso).toFormat('d LLL')

const ScoreChart = ({ points, maxScore, label }: { points: ScorePoint[]; maxScore: number; label: string }) => {
  const xScale = scalePoint<number>()
    .domain(points.map((_point, index) => index))
    .range([PADDING.left, WIDTH - PADDING.right])
    .padding(0.5)
  const yScale = scaleLinear()
    .domain([0, maxScore])
    .range([HEIGHT - PADDING.bottom, PADDING.top])
  const path =
    d3line<ScorePoint>()
      .x((_point, index) => xScale(index) ?? 0)
      .y((point) => yScale(point.score))
      .curve(curveMonotoneX)(points) ?? ''
  const baseline = HEIGHT - PADDING.bottom

  return (
    <Box
      component="svg"
      role="img"
      aria-label={`${label} scores: ${points.map((point) => point.score).join(', ')}`}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      sx={{ display: 'block', width: '100%', height: 'auto' }}
    >
      <line x1={PADDING.left} x2={WIDTH - PADDING.right} y1={baseline} y2={baseline} stroke={onDark.line} />
      {points.length > 1 && <path d={path} fill="none" stroke={onDark.secondary} strokeWidth={1.5} />}
      {points.map((point, index) => (
        <Tooltip key={point.id} title={formatDay(point.date)} placement="top">
          <Box component="g">
            <circle cx={xScale(index)} cy={yScale(point.score)} r={12} fill="transparent" />
            <circle cx={xScale(index)} cy={yScale(point.score)} r={4} fill={onDark.accentGreen} />
            <text
              x={xScale(index)}
              y={yScale(point.score) - 9}
              textAnchor="middle"
              fontSize={11}
              fontWeight={700}
              fill={onDark.primary}
            >
              {point.score}
            </text>
          </Box>
        </Tooltip>
      ))}
      {[0, points.length - 1]
        .filter((index, position, indexes) => indexes.indexOf(index) === position)
        .map((index) => (
          <text
            key={points[index].id}
            x={xScale(index)}
            y={HEIGHT - 6}
            textAnchor="middle"
            fontSize={10}
            fill={onDark.secondary}
          >
            {shortDay(points[index].date)}
          </text>
        ))}
    </Box>
  )
}

const CHANGE_COLOR: Record<TotalChangeTone, string> = {
  improved: onDark.accentGreen,
  flat: onDark.yellowAccent,
  worse: onDark.accentRed,
}

const ScoreGraphSection = ({ uid, type }: { uid: string; type: ScoreType }) => {
  const { entries, loading } = useAdvancedPatientHistoryState(uid)
  const { label, maxScore } = SCORE_TYPES[type]
  const points = scorePoints(entries, type)
  const change = totalChangeLabel(points)
  const changePercent = totalChangePercent(points)

  let body: React.ReactNode = <ScoreChart points={points} maxScore={maxScore} label={label} />
  if (loading && entries.length === 0) {
    body = <Loader size="small" />
  } else if (points.length === 0) {
    body = <EmptyNote>No scores recorded yet.</EmptyNote>
  }

  return (
    <Section
      title={label}
      action={
        change && (
          <Typography variant="subtitle2" sx={{ color: onDark.secondary }}>
            Total change{' '}
            <Box
              component="span"
              sx={{
                color: changePercent === null ? onDark.primary : CHANGE_COLOR[totalChangeTone(changePercent)],
                fontWeight: 700,
              }}
            >
              {change}
            </Box>
          </Typography>
        )
      }
    >
      {body}
    </Section>
  )
}

export default ScoreGraphSection
